import { useContext, useEffect, useState } from 'react'
import { Column, Row } from 'react-table'
import Table from '../../../global/components/table/Table'
import BasicFilter from '../../../global/components/table/helpers/filters/BasicFilter'
import CheckboxFilter from '../../../global/components/table/helpers/filters/CheckboxFilter'
import LocationFilter from '../../../global/components/table/helpers/filters/LocationFilter'
import { handleColumnFilterUpdate } from '../../../global/components/table/helpers/multiColumnFilters'
import retrieveData from '../../../global/components/table/helpers/retrieveData'
import { ILocationFilters } from '../../../work-orders/components/main/WorkOrdersMain'
import { ITechnician } from '../../../../../app/entities/Technician'
import { UserContext } from '../../../base/components/base-container/BaseContainer'
import { PERMISSIONS } from '../../../global/constants/permissions'
import hasPermission from '../../../global/utils/user/has-permission'

interface ITechnicianTableProps {
  locationFilters: ILocationFilters
  setOpen: Function
}

function TechniciansTable(props: ITechnicianTableProps) {
  const { user } = useContext(UserContext)

  const { locationFilter } = props.locationFilters
  const [tableState, setTableState] = useState({
    data: [],
    isLoading: false,
    pageSize: 100,
    offSet: 0,
    count: 0,
  })

  // Default to showing only active technicians
  const [showActiveOnly, setShowActiveOnly] = useState(true)
  const [locationColumnFilter, setLocationColumnFilter] = useState('')
  const [columnFilters, setColumnFilters] = useState({ inactive: '0' })

  const isTechEditDisabled = !hasPermission(user).allow(
    PERMISSIONS.TECHNICIANS.CATEGORY,
    PERMISSIONS.TECHNICIANS.ENTRIES.EDIT_TECHNICIANS.NAME,
  )

  const endpoint = `${process.env.REACT_APP_ORIGIN}/technicians/byWeekAndLocation?`

  useEffect(() => {
    retrieveData(setTableState, {
      endpoint,
      pageSize: tableState.pageSize,
      offSet: tableState.offSet,
      locationId: locationFilter,
      columnFilters,
    })
  }, [locationFilter, columnFilters, tableState.pageSize, tableState.offSet])

  function onColumnFilterUpdate(filtersUpdate: object) {
    handleColumnFilterUpdate(filtersUpdate, columnFilters, setColumnFilters)
  }

  function handleActiveFilterChange(checked: boolean) {
    setShowActiveOnly(checked)
    // '0' means inactive=false (active techs), '' removes the filter (show all)
    onColumnFilterUpdate({ inactive: checked ? '0' : '' })
  }

  function handleLocationFilterChange(value: string) {
    setLocationColumnFilter(value)
    onColumnFilterUpdate({ location: value ? { name: value } : '' })
  }

  const columns: Column<ITechnician>[] = [
    {
      Header: 'Employee ID',
      accessor: 'employeeId',
      Filter: BasicFilter({
        onEnter: (val: string) => onColumnFilterUpdate({ employeeId: val }),
      }),
    },
    {
      Header: 'Samsara ID',
      accessor: 'samsaraId',
      Filter: BasicFilter({
        onEnter: (val: string) => onColumnFilterUpdate({ samsaraId: val }),
      }),
    },
    {
      Header: 'Technician',
      accessor: 'name',
      Filter: BasicFilter({
        onEnter: (val: string) => onColumnFilterUpdate({ name: val }),
      }),
    },
    {
      Header: 'Status',
      accessor: (data) => (data.inactive ? 'Inactive' : 'Active'),
      Filter: CheckboxFilter({
        checked: showActiveOnly,
        onChange: handleActiveFilterChange,
        label: 'Active Only',
      }),
    },
    {
      Header: 'Location',
      accessor: 'location',
      Filter: LocationFilter({
        value: locationColumnFilter,
        onChange: handleLocationFilterChange,
      }),
    },
  ]

  return (
    <div style={{ width: '100%' }}>
      <Table
        columns={columns}
        data={tableState.data ?? []}
        isLoading={tableState.isLoading}
        onRowClick={
          isTechEditDisabled
            ? () => {}
            : (row: Row) => {
                const original = row.original as ITechnician

                props.setOpen({
                  isOpen: true,
                  techId: original.objectId,
                })
              }
        }
        isPaginated
        pagination={{ setTableState, tableState }}
        isTableTopperPresent={false}
      />
    </div>
  )
}

export default TechniciansTable
